import {useEffect} from 'react';

import {useTracking} from '@/modules/common/hooks/useTracking';

const ELEMENT_THRESHOLD = 0.5;
const ELEMENT_VISIBILITY_THRESHOLD = 1000; // 1 second

export const useSponsoredAdIntersectionTracking = (
  element: null | HTMLElement,
  {
    hotelId,
    position,
    viewableImpressionUrl,
    tourOperator,
  }: {
    hotelId: string;
    position: number;
    viewableImpressionUrl?: string;
    tourOperator?: string;
  }
) => {
  const {dispatchGenericTrackingEvent} = useTracking();
  useEffect(() => {
    if (!element || !viewableImpressionUrl) {
      return;
    }

    let visibilityTimeout: undefined | number = undefined;
    const interactionObserver = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry || !viewableImpressionUrl) continue;

          if (entry.isIntersecting) {
            // Element is 50% visible, start a 1s timer
            observer.disconnect();

            visibilityTimeout = window.setTimeout(() => {
              if (entry.intersectionRatio >= 0.5) {
                dispatchGenericTrackingEvent({
                  event: 'impression',
                  event_action: tourOperator
                    ? `${tourOperator}_sponsored_ad_viewable`
                    : 'ad_viewable',
                  ad_url: viewableImpressionUrl,
                  ad_position: `${position + 1}`,
                  giata_id: hotelId,
                });
              }
            }, ELEMENT_VISIBILITY_THRESHOLD);
          } else {
            clearTimeout(visibilityTimeout);
          }
        }
      },
      {
        root: null, // Use the viewport as the root
        rootMargin: '0px',
        threshold: [ELEMENT_THRESHOLD], // Trigger when 50% of the element is visible
      }
    );

    interactionObserver.observe(element);

    return () => {
      interactionObserver.disconnect();
    };
  }, [
    dispatchGenericTrackingEvent,
    element,
    hotelId,
    position,
    viewableImpressionUrl,
    tourOperator,
  ]);
};
